import { useState, useEffect } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertTriangle, RefreshCw, Trash2, Search, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useErrorLogs, ErrorLog } from "@/hooks/useErrorLogs";
import { ErrorSource } from "@/hooks/useErrorLogger";
import * as XLSX from "xlsx";

const sourceLabels: Record<ErrorSource, string> = {
  tablet: "Tablet",
  pdv: "PDV",
  dashboard: "Dashboard",
  public_menu: "Menu Público",
  edge_function: "Servidor",
};

const sourceColors: Record<ErrorSource, string> = {
  tablet: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  pdv: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  dashboard: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  public_menu: "bg-green-500/10 text-green-600 border-green-500/20",
  edge_function: "bg-red-500/10 text-red-600 border-red-500/20",
};

export default function ErrosPage() {
  const { logs, isLoading, totalCount, fetchLogs, deleteLog, clearAllLogs } = useErrorLogs();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sourceFilter, setSourceFilter] = useState<ErrorSource | "all">("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const pageSize = 20;

  useEffect(() => {
    fetchLogs(page, pageSize, {
      source: sourceFilter === "all" ? undefined : sourceFilter,
      dateFrom,
      dateTo,
      searchText: searchText || undefined,
    });
  }, [fetchLogs, page, sourceFilter, dateFrom, dateTo]);

  const handleSearch = () => {
    setPage(1);
    fetchLogs(1, pageSize, {
      source: sourceFilter === "all" ? undefined : sourceFilter,
      dateFrom,
      dateTo,
      searchText: searchText || undefined,
    });
  };

  const handleClearFilters = () => {
    setSearchText("");
    setSourceFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setPage(1);
    fetchLogs(1, pageSize);
  };

  const handleDelete = async (logId: string) => {
    const success = await deleteLog(logId);
    if (success) {
      toast.success("Log deletado");
    } else {
      toast.error("Erro ao deletar log");
    }
  };

  const handleClearAll = async () => {
    const success = await clearAllLogs();
    if (success) {
      toast.success("Todos os logs foram removidos");
    } else {
      toast.error("Erro ao limpar logs");
    }
  };

  const handleExport = () => {
    if (logs.length === 0) {
      toast.error("Nenhum log para exportar");
      return;
    }

    const exportData = logs.map(log => ({
      "Data/Hora": format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR }),
      "Origem": sourceLabels[log.source],
      "Código": log.error_code || "-",
      "Mensagem": log.error_message,
      "URL": log.url || "-",
      "Contexto": log.context ? JSON.stringify(log.context) : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Erros");
    XLSX.writeFile(wb, `erros_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Exportação concluída");
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <h1 className="text-2xl font-bold">Log de Erros</h1>
          <Badge variant="secondary">{totalCount} registros</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchLogs(page, pageSize)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={logs.length === 0}>
                <Trash2 className="h-4 w-4 mr-2" />
                Limpar Tudo
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Limpar todos os logs?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação é irreversível. Todos os {totalCount} registros de erro serão removidos permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Limpar Tudo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por mensagem ou código..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as ErrorSource | "all")}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="tablet">Tablet</SelectItem>
                <SelectItem value="pdv">PDV</SelectItem>
                <SelectItem value="dashboard">Dashboard</SelectItem>
                <SelectItem value="public_menu">Menu Público</SelectItem>
                <SelectItem value="edge_function">Servidor</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                  {dateFrom ? format(dateFrom, "dd/MM/yyyy") : "Data início"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                  {dateTo ? format(dateTo, "dd/MM/yyyy") : "Data fim"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
              </PopoverContent>
            </Popover>

            <Button onClick={handleSearch}>Buscar</Button>
            <Button variant="ghost" onClick={handleClearFilters}>Limpar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Data/Hora</TableHead>
                <TableHead className="w-[120px]">Origem</TableHead>
                <TableHead className="w-[120px]">Código</TableHead>
                <TableHead>Mensagem</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    Nenhum erro registrado
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedLog(log)}
                  >
                    <TableCell className="font-mono text-sm">
                      {format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={sourceColors[log.source]}>
                        {sourceLabels[log.source]}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.error_code || "-"}
                    </TableCell>
                    <TableCell className="max-w-[400px] truncate">
                      {log.error_message}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(log.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal de detalhes */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Detalhes do Erro
            </DialogTitle>
            <DialogDescription>
              {selectedLog && format(new Date(selectedLog.created_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Origem</p>
                  <Badge variant="outline" className={sourceColors[selectedLog.source]}>
                    {sourceLabels[selectedLog.source]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Código</p>
                  <p className="font-mono">{selectedLog.error_code || "-"}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Mensagem</p>
                <p className="bg-muted p-3 rounded-md text-sm">{selectedLog.error_message}</p>
              </div>

              {selectedLog.url && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">URL</p>
                  <p className="bg-muted p-3 rounded-md text-sm font-mono break-all">{selectedLog.url}</p>
                </div>
              )}

              {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Contexto</p>
                  <pre className="bg-muted p-3 rounded-md text-sm font-mono overflow-x-auto">
                    {JSON.stringify(selectedLog.context, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.error_stack && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Stack Trace</p>
                  <pre className="bg-muted p-3 rounded-md text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {selectedLog.error_stack}
                  </pre>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">User Agent</p>
                  <p className="bg-muted p-3 rounded-md text-xs font-mono break-all">{selectedLog.user_agent}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
